<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import playerService from '../services/player.service'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || '')
const playerUuid = computed(() => route.params.uuid as string)
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)
const possibleCategories = computed(() => authStore.initData?.possible_categories || [])

const player = ref<any>(null)
const loading = ref(false)
const attendanceStats = ref<any>(null)
const loadingStats = ref(false)
const recentAttendance = ref<any[]>([])
const loadingAttendance = ref(false)
const payments = ref<any[]>([])
const loadingPayments = ref(false)

// Selected month for stats (default to current month)
const selectedMonth = ref<string>('')

// Pagination for attendance
const perPage = ref(25)
const currentPage = ref(1)
const lastPage = ref(1)
const totalEvents = ref(0)

// Check if category is accessible
const isCategoryAccessible = (categoryUuid: string) => {
  // OWNER and SUPERVISOR can access all categories
  if (currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR') {
    return true
  }
  // COACH checks possible_categories
  return possibleCategories.value.includes(categoryUuid)
}

// Calculate age
const calculateAge = (birthDate: string): number => {
  const birth = new Date(birthDate)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age
}

const age = computed(() => {
  if (player.value?.birth_date) {
    return calculateAge(player.value.birth_date)
  }
  return null
})

// Visible pages for pagination
const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []

  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(lastPage.value, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Calculate medical exam status
const examStatus = computed(() => {
  if (!player.value?.medical_exam_date) return null

  const examDate = new Date(player.value.medical_exam_date)
  const today = new Date()
  const diffTime = examDate.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  const isValid = diffDays > 0

  return {
    isValid,
    daysLeft: Math.abs(diffDays)
  }
})

// Fetch player payments
const fetchPlayerPayments = async () => {
  if (!clubUuid.value || !playerUuid.value) return

  loadingPayments.value = true
  try {
    const response = await playerService.getPlayerPayments(clubUuid.value, playerUuid.value)
    payments.value = response.data || []
  } catch (err: any) {
    console.error('Error fetching player payments:', err)
  } finally {
    loadingPayments.value = false
  }
}

// Fetch player data
const fetchPlayer = async () => {
  if (!clubUuid.value || !playerUuid.value) return

  loading.value = true
  try {
    player.value = await playerService.getPlayer(clubUuid.value, playerUuid.value)
  } catch (err: any) {
    console.error('Error fetching player:', err)
    toastStore.error(t('playerDetail.errorFetchingPlayer'))
    router.push('/dashboard')
  } finally {
    loading.value = false
  }
}

// Copy to clipboard
const copyToClipboard = (text: string, type: string) => {
  navigator.clipboard.writeText(text).then(() => {
    if (type === 'email') {
      toastStore.success(t('playerDetail.emailCopied'))
    } else if (type === 'phone') {
      toastStore.success(t('playerDetail.phoneCopied'))
    } else {
      toastStore.success(t('playerDetail.copied'))
    }
  }).catch(err => {
    console.error('Failed to copy:', err)
    toastStore.error(t('playerDetail.copyFailed'))
  })
}

// Send invitation
const sendInvitation = async () => {
  if (!player.value?.email) {
    toastStore.error(t('playerDetail.noEmailError'))
    return
  }

  if (!clubUuid.value || !playerUuid.value) {
    toastStore.error(t('playerDetail.errorSendingInvitation'))
    return
  }

  try {
    const response = await playerService.resendInvitation(clubUuid.value, playerUuid.value)
    toastStore.success(t('playerDetail.invitationSent'))
  } catch (err: any) {
    console.error('Error resending invitation:', err)
    const errorMessage = err.response?.data?.message || t('playerDetail.errorSendingInvitation')
    toastStore.error(errorMessage)
  }
}

// Format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('pl-PL')
}

// Navigate to edit
const goToEdit = () => {
  router.push(`/players/${playerUuid.value}/edit`)
}

// Fetch attendance stats for selected month
const fetchAttendanceStats = async () => {
  if (!clubUuid.value || !playerUuid.value) return

  loadingStats.value = true

  try {
    // Use selected month or convert YYYY-MM to YYYY-MM-DD format (first day of month)
    const dateString = selectedMonth.value ? `${selectedMonth.value}-01` : new Date().toISOString().split('T')[0]

    const response = await playerService.getAttendanceStats(clubUuid.value, playerUuid.value, dateString)
    attendanceStats.value = response
  } catch (err: any) {
    console.error('Error fetching attendance stats:', err)
  } finally {
    loadingStats.value = false
  }
}

// Handle month change
const onMonthChange = () => {
  if (currentRole.value !== 'PLAYER') {
    fetchAttendanceStats()
  }
}

// Initialize selected month to current month
const initializeMonth = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  selectedMonth.value = `${year}-${month}`
}

// Fetch recent attendance with pagination
const fetchRecentAttendance = async (page: number = 1) => {
  if (!clubUuid.value || !playerUuid.value) return

  loadingAttendance.value = true

  try {
    const response = await playerService.getRecentAttendance(clubUuid.value, playerUuid.value, perPage.value, page)
    recentAttendance.value = response.data || []
    currentPage.value = response.current_page || page
    lastPage.value = response.last_page || 1
    totalEvents.value = response.total || 0
  } catch (err: any) {
    console.error('Error fetching recent attendance:', err)
  } finally {
    loadingAttendance.value = false
  }
}

// Handle page change
const changePage = (page: number) => {
  currentPage.value = page
  fetchRecentAttendance(page)
}

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1)
  }
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    changePage(currentPage.value + 1)
  }
}

// Handle per page change
const changePerPage = () => {
  currentPage.value = 1
  fetchRecentAttendance(1)
}

// Format datetime to readable format
const formatDateTime = (dateTimeString: string | null) => {
  if (!dateTimeString) return '-'
  const date = new Date(dateTimeString)
  return date.toLocaleDateString('pl-PL') + ' ' + date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
}

onMounted(() => {
  initializeMonth()
  fetchPlayer()

  // Only fetch stats, attendance and payments if not PLAYER
  if (currentRole.value !== 'PLAYER') {
    fetchAttendanceStats()
    fetchRecentAttendance()

    // Only fetch payments if not COACH
    if (currentRole.value !== 'COACH') {
      fetchPlayerPayments()
    }
  }
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4">
      <!-- Back Button (hidden for PLAYER role) -->
      <div v-if="currentRole !== 'PLAYER'" class="mb-4">
        <router-link to="/dashboard" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('playerDetail.backToList') }}
        </router-link>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>

      <template v-else-if="player">
        <!-- Header with Actions -->
        <div class="mb-4 flex flex-wrap justify-between items-start gap-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ player.first_name }} {{ player.last_name }}</h1>
            <p v-if="age !== null" class="text-sm text-gray-600">{{ age }} {{ t('playerDetail.yearsOld') }}</p>
          </div>
          <div v-if="currentRole === 'OWNER' || currentRole === 'SUPERVISOR'" class="flex gap-2">
            <button @click="goToEdit" class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 border-2 border-blue-600">
              {{ t('playerDetail.edit') }}
            </button>
          </div>
        </div>

        <!-- Registration Status Banner (hidden for PLAYER role) -->
        <!-- Registered -->
        <div v-if="currentRole !== 'PLAYER' && player.registration_status === 'registered'" class="bg-green-50 border-2 border-green-300 rounded p-3 mb-4">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="text-sm font-medium text-green-800">{{ t('playerDetail.userRegistered') }}</span>
          </div>
        </div>
        <!-- Not Registered -->
        <div v-else-if="currentRole !== 'PLAYER'" class="bg-red-50 border-2 border-red-300 rounded p-3 mb-4">
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
              </svg>
              <span class="text-sm font-medium text-red-800">{{ t('playerDetail.userNotRegistered') }}</span>
            </div>
            <button
              v-if="currentRole === 'OWNER' || currentRole === 'SUPERVISOR'"
              @click="sendInvitation"
              class="px-3 py-1.5 bg-blue-600 text-xs font-medium text-white rounded hover:bg-blue-700"
            >
              {{ t('playerDetail.sendInvitation') }}
            </button>
          </div>
        </div>

        <!-- Player Data - 3 columns -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <!-- Column 1: Basic Info -->
          <div class="space-y-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('playerDetail.basicData') }}</h2>
              <div class="flex gap-4">
                <!-- Photo -->
                <div class="flex-shrink-0">
                  <img v-if="player.photo" :src="player.photo" :alt="`${player.first_name} ${player.last_name}`" class="w-20 h-20 rounded object-cover">
                  <div v-else class="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
                    <svg class="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                </div>
                <!-- Data -->
                <div class="flex-1 space-y-2 text-sm">
                  <div>
                    <span class="text-gray-600">{{ t('playerDetail.gender') }}</span>
                    <span class="ml-2 font-medium">{{ player.gender === 'M' ? t('playerDetail.male') : t('playerDetail.female') }}</span>
                  </div>
                  <div>
                    <span class="text-gray-600">{{ t('playerDetail.birthDate') }}</span>
                    <span class="ml-2 font-medium">{{ player.birth_date ? formatDate(player.birth_date) : '-' }}</span>
                  </div>
                </div>
              </div>
              <div class="mt-3 text-sm">
                <span class="text-gray-600">{{ t('playerDetail.categories') }}</span>
                <div class="mt-1 flex flex-wrap gap-1">
                  <template v-for="category in player.categories" :key="category.uuid">
                    <router-link
                      v-if="isCategoryAccessible(category.uuid)"
                      :to="`/categories/${category.uuid}`"
                      class="inline-block px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded cursor-pointer hover:bg-gray-300 transition-colors"
                    >
                      {{ category.name }}
                    </router-link>
                    <span
                      v-else
                      class="inline-block px-2 py-1 bg-gray-200 text-gray-500 text-xs rounded opacity-60"
                    >
                      {{ category.name }}
                    </span>
                  </template>
                  <span v-if="!player.categories || player.categories.length === 0" class="text-gray-500 text-xs">{{ t('playerDetail.noCategories') }}</span>
                </div>
              </div>
              <div v-if="player.coaches && player.coaches.length > 0" class="mt-3 text-sm">
                <span class="text-gray-600">{{ t('playerDetail.coaches') }}</span>
                <div class="mt-1 space-y-1">
                  <div v-for="coach in player.coaches" :key="coach.uuid" class="text-gray-900">
                    {{ coach.first_name }} {{ coach.last_name }}<span v-if="coach.phone"> (tel. {{ coach.phone }})</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Guardians -->
            <div v-if="player.guardians && player.guardians.length > 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">
                {{ player.guardians[0]?.guardian_type === 'parent' ? t('playerDetail.parents') : t('playerDetail.legalGuardians') }}
              </h2>
              <div class="space-y-2 text-sm">
                <div v-for="guardian in player.guardians" :key="guardian.uuid">
                  <span class="text-gray-600">{{ guardian.guardian_number }}.</span>
                  <span class="ml-2 font-medium">{{ guardian.first_name }} {{ guardian.last_name }}</span>
                  <a v-if="guardian.phone" :href="`tel:${guardian.phone}`" class="ml-2 text-blue-600 hover:underline">{{ guardian.phone }}</a>
                </div>
              </div>
            </div>
          </div>

          <!-- Column 2: Contact & Medical -->
          <div class="space-y-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('playerDetail.contactData') }}</h2>
              <div class="space-y-2 text-sm">
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-gray-600">{{ t('playerDetail.email') }}</span>
                    <span class="ml-2 font-medium">{{ player.email || '-' }}</span>
                  </div>
                  <button
                    v-if="player.email"
                    @click="copyToClipboard(player.email, 'email')"
                    class="ml-2 p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    :title="t('playerDetail.copyEmail')"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                </div>
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-gray-600">{{ t('playerDetail.phone') }}</span>
                    <span class="ml-2 font-medium">{{ player.phone || '-' }}</span>
                  </div>
                  <button
                    v-if="player.phone"
                    @click="copyToClipboard(player.phone, 'phone')"
                    class="ml-2 p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    :title="t('playerDetail.copyPhone')"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                </div>
                <div>
                  <span class="text-gray-600">{{ t('playerDetail.licenseNumber') }}</span>
                  <span class="ml-2 font-medium">{{ player.license_number || '-' }}</span>
                </div>
              </div>
            </div>

            <!-- Medical Exam -->
            <div v-if="player.medical_exam_date" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('playerDetail.medicalExam') }}</h2>
              <div class="flex items-center justify-between">
                <div class="text-sm">
                  <span class="text-gray-600">{{ t('playerDetail.validUntil') }}</span>
                  <span class="ml-2 font-medium">{{ formatDate(player.medical_exam_date) }}</span>
                </div>
                <span
                  v-if="examStatus"
                  :class="[
                    'px-2 py-1 text-xs rounded border',
                    examStatus.isValid && examStatus.daysLeft <= 30 ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                    examStatus.isValid ? 'bg-green-100 text-green-800 border-green-300' :
                    'bg-red-100 text-red-800 border-red-300'
                  ]"
                >
                  <template v-if="examStatus.isValid">
                    <template v-if="examStatus.daysLeft <= 30">{{ t('playerDetail.expiringInDays').replace('{days}', examStatus.daysLeft.toString()) }}</template>
                    <template v-else>{{ t('playerDetail.valid') }}</template>
                  </template>
                  <template v-else>{{ t('playerDetail.expiredDays').replace('{days}', examStatus.daysLeft.toString()) }}</template>
                </span>
              </div>
            </div>
          </div>

          <!-- Column 3: Address & Stats -->
          <div class="space-y-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('playerDetail.addressAndSchool') }}</h2>
              <div class="space-y-2 text-sm">
                <div v-if="player.street">
                  <span class="text-gray-600">{{ t('playerDetail.street') }}</span>
                  <span class="ml-2 font-medium">{{ player.street }}</span>
                </div>
                <div v-if="player.city || player.postal_code">
                  <span class="text-gray-600">{{ t('playerDetail.city') }}</span>
                  <span class="ml-2 font-medium">{{ player.postal_code }} {{ player.city }}</span>
                </div>
                <div v-if="player.school_name">
                  <span class="text-gray-600">{{ t('playerDetail.school') }}</span>
                  <span class="ml-2 font-medium">{{ player.school_name }}</span>
                </div>
                <div v-if="!player.street && !player.city && !player.postal_code && !player.school_name" class="text-gray-500 text-xs">
                  {{ t('playerDetail.noAddressData') }}
                </div>
              </div>
            </div>

            <!-- Quick Stats (hidden for PLAYER role) -->
            <div v-if="currentRole !== 'PLAYER'" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <div class="flex items-center justify-between mb-3">
                <h2 class="text-base font-semibold text-gray-900">{{ t('playerDetail.statistics') }}</h2>
                <input
                  type="month"
                  v-model="selectedMonth"
                  @change="onMonthChange"
                  class="px-2 py-1 text-xs border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                />
              </div>
              <div class="space-y-3">
                <div class="text-center p-3 bg-blue-50 rounded">
                  <div v-if="loadingStats" class="text-2xl font-bold text-blue-600">
                    <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                  <div v-else class="text-2xl font-bold text-blue-600">{{ attendanceStats?.total_events || 0 }}</div>
                  <div class="text-xs text-gray-600">{{ t('playerDetail.trainingsSelectedMonth') }}</div>
                </div>
                <div class="text-center p-3 bg-green-50 rounded">
                  <div v-if="loadingStats" class="text-2xl font-bold text-green-600">
                    <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                  </div>
                  <div v-else class="text-2xl font-bold text-green-600">{{ attendanceStats?.attendance_percentage || 0 }}%</div>
                  <div class="text-xs text-gray-600">{{ t('playerDetail.attendance') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Payments -->
        <div v-if="currentRole !== 'COACH' && currentRole !== 'PLAYER'" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 mb-4">
          <div class="flex items-center justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-900">{{ t('playerDetail.recentPayments') }}</h2>
            <router-link to="/payments" class="px-3 py-1.5 bg-blue-600 text-xs font-medium text-white rounded hover:bg-blue-700">
              {{ t('playerDetail.viewAllPayments') }}
            </router-link>
          </div>

          <!-- Loading state -->
          <div v-if="loadingPayments" class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            <p class="mt-2 text-sm text-gray-600">{{ t('playerDetail.loadingPayments') }}</p>
          </div>

          <!-- No payments -->
          <div v-else-if="payments.length === 0" class="text-center py-8">
            <p class="text-sm text-gray-600">{{ t('playerDetail.noPayments') }}</p>
          </div>

          <!-- Payments list -->
          <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <router-link
              v-for="payment in payments"
              :key="payment.uuid"
              :to="`/payment/${payment.uuid}/${payment.hash}`"
              class="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50"
            >
              <div class="flex-1 min-w-0">
                <div class="text-sm text-gray-900 truncate">{{ payment.name }}</div>
                <div class="text-xs text-gray-500">
                  {{ payment.amount }} {{ payment.currency || 'PLN' }} • {{ t('playerDetail.dueDate') }} {{ formatDate(payment.due_date) }}
                </div>
                <div v-if="payment.paid_at" class="text-xs text-green-600">
                  {{ t('playerDetail.paidOn') }} {{ formatDate(payment.paid_at) }}
                </div>
              </div>
              <div class="ml-2 flex-shrink-0">
                <span
                  :class="[
                    'px-2 py-1 text-xs font-medium rounded border',
                    (payment.status === 'paid' || payment.paid_at) ? 'bg-green-100 text-green-800 border-green-300' : 'bg-red-100 text-red-800 border-red-300'
                  ]"
                >
                  {{ (payment.status === 'paid' || payment.paid_at) ? t('playerDetail.paid') : t('playerDetail.unpaid') }}
                </span>
              </div>
            </router-link>
          </div>
        </div>

        <!-- Attendance List with Pagination -->
        <div v-if="currentRole !== 'PLAYER'" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
          <div class="mb-3">
            <h2 class="text-base font-semibold text-gray-900">{{ t('playerDetail.attendanceList') }}</h2>
          </div>

          <!-- Loading state -->
          <div v-if="loadingAttendance" class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            <p class="mt-2 text-sm text-gray-600">{{ t('playerDetail.loadingAttendance') }}</p>
          </div>

          <!-- No data -->
          <div v-else-if="recentAttendance.length === 0" class="text-center py-8">
            <p class="text-sm text-gray-600">{{ t('playerDetail.noRecentAttendance') }}</p>
          </div>

          <!-- Attendance list -->
          <div v-else>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
              <div v-for="event in recentAttendance" :key="event.uuid" class="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50">
                <div class="flex-1 min-w-0">
                  <router-link :to="`/events/${event.uuid}`" class="text-sm text-gray-900 hover:underline truncate block">
                    {{ event.title }}
                  </router-link>
                  <div class="text-xs text-gray-500">{{ formatDateTime(event.start) }}</div>
                  <div v-if="event.place" class="text-xs text-gray-500">{{ event.place }}</div>
                  <div v-if="!event.is_present && event.absence_reason" class="text-xs text-red-600 italic">{{ event.absence_reason }}</div>
                </div>
                <div class="ml-2 flex-shrink-0">
                  <span v-if="event.is_present" class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded border border-green-300">{{ t('playerDetail.present') }}</span>
                  <span v-else class="px-2 py-1 bg-red-100 text-red-800 text-xs rounded border border-red-300">{{ t('playerDetail.absent') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="!loadingAttendance && totalEvents > 0" class="mt-4 flex justify-end items-center gap-4">
          <div class="flex items-center gap-2">
            <label for="perPage" class="text-sm text-gray-700">{{ t('playerDetail.eventsPerPage') }}:</label>
            <select id="perPage" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </template>
    </div>
  </main>
</template>
